import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRedeemDetails, isApiSuccess } from "../../services/api";

/**
 * Turns the /loyalty/redeem-details response into one flat view-model, so the
 * Confirmation page never touches the raw API shape.
 *
 * purchaseType: "reward" (bill payment with reward / redemption), "membership"
 * (membership bought on its own), "payment" (bill paid without any reward).
 * When the response changes for "voucher" purchases, this is the only place that
 * needs to learn the new shape, then add its copy in Confirmation.jsx (PURCHASE_COPY).
 */
export const normalizeConfirmation = (res) => {
    const redemption = res?.data || null;
    const userCard = res?.UserCard || null;
    const txn = res?.transactions || null;
    const program = userCard?.usercard?.loyalty_program || null;

    // mep = 1 / true when a membership was bought on this order
    const mepFlag = Number(res?.mep) === 1;
    // membership bought on its own: no bill (data is null), just the card (and/or mep flag)
    const membershipOnly = !redemption && (mepFlag || Boolean(userCard));
    // this response may have no mep field, so a membership-only order counts as a membership purchase
    const mep = mepFlag || membershipOnly;

    // bill paid without any reward: no redemption (data null), no card, only the transaction
    const paymentOnly = !redemption && !membershipOnly && Boolean(txn);
    const paymentDone = Number(res?.payment) === 1 || Number(txn?.approved) === 1;

    const purchaseType = redemption ? "reward" : membershipOnly ? "membership" : paymentOnly ? "payment" : "unknown";

    let statusRaw;
    if (redemption) {
        statusRaw = String(redemption.status || "").toLowerCase();
    } else if (membershipOnly) {
        // card active = membership bought. With no card in the response, fall back to API success
        const done = userCard
            ? String(userCard.status || "").toLowerCase() === "active"
            : Boolean(res?.status?.success);
        statusRaw = done ? "completed" : "pending";
    } else if (paymentOnly) {
        // no redemption row to read a status from: the transaction itself says whether it went through
        statusRaw = paymentDone ? "completed" : "pending";
    } else {
        statusRaw = "";
    }

    return {
        purchaseType,
        mep,
        rdm: Number(res?.rdm) || 0,
        payment: Number(res?.payment) || 0,
        statusRaw,
        // anything other than "completed" is treated as pending
        status: statusRaw === "completed" ? "completed" : "pending",
        referenceNo: redemption?.referneceNo || res?.ref || txn?.RefrenceNo || null,
        transactionId: redemption?.redem_id || null,
        date: redemption?.redemption_date || txn?.created_at || userCard?.activation_date || null,
        storeId: redemption?.stores_id ?? null,
        rewardId: redemption?.reward_id ?? null,
        // paid bill without reward: the bill amount only exists on the transaction
        orderAmount: Number(redemption?.order_amount) || (paymentOnly ? Number(txn?.amount) || 0 : 0),
        discount: Number(redemption?.discount) || 0,
        cashbackAmount: Number(res?.cashback ?? res?.cashback_pending) || 0,
        cashbackApproved: Number(res?.cashback_status) === 1, // true / 1 = approved, false / 0 / missing = pending
        discountCustomer: redemption?.discountCust ?? null,
        discountOwner: redemption?.discountOwner ?? null,
        pointsDeducted: Number(redemption?.points_deducted) || 0,
        // raw pieces the confirmation page reads for the redeem details rows
        redemption,
        transaction: txn,
        membership: userCard
            ? {
                  programName: program?.program_name || "Membership",
                  description: program?.description || null,
                  tagline: program?.card_tagline || null,
                  logo: program?.membership_logo || null,
                  membershipType: program?.membership_type || null, // "paid" | "free"
                  membershipAmount: Number(program?.membership_amount) || 0,
                  welcomeCoin: Number(program?.welcome_coin) || 0,
                  services: Array.isArray(program?.services) ? program.services : [],
                  cardNumber: userCard.card_number || "",
                  cardType: userCard.usercard?.card_type || null,
                  isPrimary: Boolean(userCard.is_primary_card),
                  status: userCard.status || null,
                  activationDate: userCard.activation_date || null,
                  expiryDate: userCard.deactivation_date || userCard.usercard?.expiration_date || null,
                  pointsBalance: Number(userCard.points_balance) || 0,
              }
            : null,
        // Voucher deduction
        usedVoucher:
            res?.UsedVoucher === true ||
            Number(res?.UsedVoucher) === 1,
        voucherAmount:
            res?.UsedVoucher === true ||
            Number(res?.UsedVoucher) === 1
                ? Number(res?.VoucherAmount) || 0
                : 0,
    };
};

export const fetchPaymentConfirmation = createAsyncThunk(
    "paymentConfirmation/fetch",
    async (referenceNo, { rejectWithValue }) => {
        try {
            const res = await getRedeemDetails(referenceNo);
            if (!isApiSuccess(res)) {
                return rejectWithValue(res?.message || "Unable to load payment details.");
            }
            return normalizeConfirmation(res);
        } catch (err) {
            return rejectWithValue(err?.message || "Unable to load payment details.");
        }
    }
);

const initialState = {
    referenceNo: null, // reference the current `details` belong to
    details: null,
    status: "idle", // idle | loading | succeeded | failed
    error: null,
};

const paymentConfirmationSlice = createSlice({
    name: "paymentConfirmation",
    initialState,
    reducers: {
        clearPaymentConfirmation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaymentConfirmation.pending, (state, action) => {
                const ref = action.meta.arg;
                if (state.referenceNo !== ref) {
                    // new reference: drop the old result
                    state.referenceNo = ref;
                    state.details = null;
                    state.status = "loading";
                } else if (!state.details) {
                    state.status = "loading";
                }
                // same reference with data already shown (refresh/poll): keep showing it, no flicker
                state.error = null;
            })
            .addCase(fetchPaymentConfirmation.fulfilled, (state, action) => {
                state.details = action.payload;
                state.status = "succeeded";
            })
            .addCase(fetchPaymentConfirmation.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || action.error?.message || "Unable to load payment details.";
            });
    },
});

export const { clearPaymentConfirmation } = paymentConfirmationSlice.actions;
export default paymentConfirmationSlice.reducer;